import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import api from '../utils/api';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { user } = useAuth();
  const { tenant } = useTenant();
  const userIdRef = useRef(null);
  const joinedRoomsRef = useRef(new Set());
  const tenantIdRef = useRef(null);

  // Suivre le tenant actuel via ref (pour les callbacks sans re-render)
  useEffect(() => {
    tenantIdRef.current = tenant?.id || null;
  }, [tenant?.id]);

  // Charger les notifications persistées depuis l'API (recharger quand le tenant change)
  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const params = {};
        if (tenant?.id) params.organizationId = tenant.id;
        const res = await api.get('/notifications', { params });
        const mapped = res.data.map(n => ({
          id: n.id,
          type: n.type,
          title: n.title,
          message: n.message,
          link: n.link,
          timestamp: n.created_at,
          read: !!n.is_read,
          organization_id: n.organization_id
        }));
        setNotifications(mapped);
      } catch (err) {
        console.error('Erreur chargement notifications:', err);
      }
    };
    fetchNotifications();
  }, [user?.id, tenant?.id]);

  useEffect(() => {
    if (!user?.id) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      userIdRef.current = null;
      return;
    }

    // Ne pas recréer si même utilisateur
    if (userIdRef.current === user.id && socket) {
      return;
    }

    userIdRef.current = user.id;

    if (socket) {
      socket.disconnect();
    }

    const token = localStorage.getItem('token');
    const newSocket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      auth: { token }
    });

    const rejoinRooms = () => {
      if (user.organization_id) {
        newSocket.emit('join_organization', user.organization_id);
      }
      joinedRoomsRef.current.forEach(ticketNumber => {
        newSocket.emit('join_ticket', ticketNumber);
      });
    };

    newSocket.on('connect', () => {
      console.log('🔌 WebSocket connecté:', newSocket.id);
      setConnected(true);
      rejoinRooms();
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket déconnecté:', reason);
      setConnected(false);
    });

    newSocket.on('connect_error', (err) => {
      console.log('🔌 Erreur connexion WebSocket:', err.message);
    });

    // Écouter les événements WebSocket (pour refresh UI dans les composants)
    newSocket.on('new_ticket', () => {});
    newSocket.on('new_message', () => {});
    newSocket.on('ticket_assigned', () => {});
    newSocket.on('mention', () => {});
    newSocket.on('status_changed', () => {});
    newSocket.on('ticket_updated', () => {});

    // Écouter les notifications persistées poussées par le serveur
    newSocket.on('notification', (notif) => {
      // Filtrer par organisation si un tenant est sélectionné
      if (tenantIdRef.current && notif.organization_id && notif.organization_id !== tenantIdRef.current) {
        return; // Ne pas afficher les notifications d'une autre org
      }

      const mapped = {
        id: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        link: notif.link,
        timestamp: notif.created_at || new Date(),
        read: false,
        organization_id: notif.organization_id
      };
      setNotifications(prev => {
        // Éviter les doublons
        if (prev.some(n => n.id === mapped.id)) return prev;
        return [mapped, ...prev].slice(0, 50);
      });

      // Son de notification
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}

      // Notification navigateur
      if (Notification.permission === 'granted') {
        new Notification(notif.title, {
          body: notif.message,
          icon: '/favicon.ico'
        });
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user?.id, user?.organization_id]);

  const addNotification = useCallback((notification) => {
    const newNotif = {
      ...notification,
      id: `ws_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev].slice(0, 50));

    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {}

    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico'
      });
    }
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    // Persister côté serveur si c'est un ID numérique (venant de la BDD)
    if (typeof notificationId === 'number') {
      try {
        await api.post(`/notifications/${notificationId}/read`);
      } catch (err) {
        console.error('Erreur markAsRead:', err);
      }
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      const params = {};
      if (tenantIdRef.current) params.organizationId = tenantIdRef.current;
      await api.post('/notifications/read-all', null, { params });
    } catch (err) {
      console.error('Erreur markAllAsRead:', err);
    }
  }, []);

  const clearNotifications = useCallback(async () => {
    setNotifications([]);
    try {
      const params = {};
      if (tenantIdRef.current) params.organizationId = tenantIdRef.current;
      await api.delete('/notifications/all', { params });
    } catch (err) {
      console.error('Erreur clearNotifications:', err);
    }
  }, []);

  const joinTicket = useCallback((ticketNumber) => {
    joinedRoomsRef.current.add(ticketNumber);
    if (socket?.connected) {
      socket.emit('join_ticket', ticketNumber);
    }
  }, [socket]);

  const leaveTicket = useCallback((ticketNumber) => {
    joinedRoomsRef.current.delete(ticketNumber);
    if (socket?.connected) {
      socket.emit('leave_ticket', ticketNumber);
    }
  }, [socket]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <SocketContext.Provider value={{
      socket,
      connected,
      notifications,
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearNotifications,
      joinTicket,
      leaveTicket
    }}>
      {children}
    </SocketContext.Provider>
  );
};
